import { useEffect, useState } from "react";
import { api } from "../../api/client";
import {
  Badge,
  useFlashOnChange,
  Button,
  DataCard,
  Empty,
  Icon,
  Input,
  Modal,
  PageTitle,
  Select,
  Spinner,
  Td,
  Th,
  cx
} from "../../components/ui";

// ── Staff Payroll ───────────────────────────────────────────────────────────
//
// Gaji staff NON-TRAINER: dihitung dari sesi yang DICENTANG admin × tarif di Data
// Setting. Berbeda dari payroll trainer yang diturunkan dari absensi murid —
// di sini tak ada yang bisa diturunkan, kehadiran staff memang harus dinyatakan.
//
// Dua tampilan, karena dua pekerjaan yang berbeda ritmenya: mencentang itu HARIAN,
// memeriksa total itu BULANAN. Digabung jadi satu tabel 31×5 per orang berarti 155
// kotak dalam satu layar untuk pekerjaan yang sebenarnya sekali-sehari.

const RUPIAH = new Intl.NumberFormat("id-ID");
const rp = (n) => "Rp " + RUPIAH.format(Math.round(Number(n) || 0));

const BULAN = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember"
];

function hariIni() {
  const d = new Date();
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export default function StaffPayrollPage({ setToast, isSuperAdmin = false }) {
  const [tab, setTab] = useState("harian");
  // Dinaikkan tiap kali penunjukan berubah, supaya kedua tampilan ikut memuat ulang —
  // menunjuk orang baru harus langsung terlihat di daftar centang.
  const [versi, setVersi] = useState(0);

  return (
    <>
      <PageTitle
        title="Staff Payroll"
        subtitle="Gaji staff non-trainer: sesi yang dicentang admin × tarif di Data Settings."
      />

      <div className="mb-4 flex gap-2">
        {[
          ["harian", "event_available", "Per Tanggal"],
          ["rekap", "receipt_long", "Rekap Bulan"],
          ["orang", "badge", "Staff & Sesi"]
        ].map(([key, ikon, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cx(
              "inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition",
              tab === key
                ? "bg-primary text-white"
                : "bg-slate-100 text-slate-500 hover:text-primary dark:bg-slate-800"
            )}
          >
            <Icon name={ikon} className="text-base" />
            {label}
          </button>
        ))}
      </div>

      {tab === "harian" && <TabHarian setToast={setToast} versi={versi} />}
      {tab === "rekap" && (
        <TabRekap setToast={setToast} versi={versi} isSuperAdmin={isSuperAdmin} />
      )}
      {tab === "orang" && (
        <TabOrang setToast={setToast} onBerubah={() => setVersi((v) => v + 1)} />
      )}
    </>
  );
}

// ── Per tanggal: daftar staff × lima sesi ───────────────────────────────────
function TabHarian({ setToast, versi }) {
  const [tanggal, setTanggal] = useState(hariIni());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sibuk, setSibuk] = useState("");

  async function load() {
    setLoading(true);
    try {
      const res = await api.getStaffMarks(tanggal);
      if (res?.success) setData(res);
      else setToast({ type: "error", message: res?.message || "Gagal memuat" });
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal memuat" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tanggal, versi]);

  async function toggle(userId, session, dicentang) {
    const kunci = `${userId}||${session}`;
    setSibuk(kunci);
    try {
      const res = await api.setStaffMark({
        userId,
        date: tanggal,
        session,
        checked: !dicentang
      });
      if (res?.success === false) {
        setToast({ type: "error", message: res.message });
      } else if (res?.message && /masih 0/.test(res.message)) {
        // Tarif belum diisi — dikatakan sekali, tidak disembunyikan.
        setToast({ type: "error", message: res.message });
      }
      load();
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal menyimpan" });
      load();
    } finally {
      setSibuk("");
    }
  }

  const orang = data?.orang || [];
  const sessions = data?.sessions || [];
  const totalHari = orang.reduce(
    (a, o) => a + o.sesi.filter((s) => s.dicentang).reduce((b, s) => b + Number(s.rate || 0), 0),
    0
  );
  // Tiap centang mengubah angka ini. Menyorotnya membuat akibat dari klik tadi
  // terlihat, tanpa perlu mencari-cari di pojok layar.
  const kilatHari = useFlashOnChange(totalHari);

  return (
    <DataCard>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-5 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <Input
            type="date"
            value={tanggal}
            onChange={(e) => setTanggal(e.target.value)}
            className="w-auto"
          />
          {data?.terkunci && <Badge>Bulan ini terkunci</Badge>}
        </div>
        <div className="text-sm">
          <span className="text-slate-500">Total hari ini: </span>
          <span className={cx("font-black text-primary", kilatHari && "anim-flash")}>
            {rp(totalHari)}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="p-5">
          <Spinner />
        </div>
      ) : !orang.length ? (
        <div className="p-5">
          <Empty label="Belum ada staff yang ditunjuk di cabang ini. Buka tab Staff & Sesi." />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] whitespace-nowrap text-left">
            <thead>
              <tr>
                <Th sticky>Staff</Th>
                {sessions.map((s) => (
                  <Th key={s} center>
                    {s}
                  </Th>
                ))}
                <Th right>Total</Th>
              </tr>
            </thead>
            <tbody>
              {orang.map((o) => {
                const total = o.sesi
                  .filter((s) => s.dicentang)
                  .reduce((a, s) => a + Number(s.rate || 0), 0);
                return (
                  <tr
                    key={o.userId}
                    className="border-t border-slate-200 hover:bg-primary/[0.03] dark:border-slate-800 dark:hover:bg-primary/[0.06]"
                  >
                    <Td sticky>
                      <div className="font-bold">{o.nama}</div>
                      <div className="text-[11px] text-slate-500">{o.staffPosition}</div>
                    </Td>
                    {o.sesi.map((s) => (
                      <Td key={s.session} center>
                        <button
                          disabled={data.terkunci || sibuk === `${o.userId}||${s.session}`}
                          onClick={() => toggle(o.userId, s.session, s.dicentang)}
                          title={
                            data.terkunci
                              ? "Bulan ini sudah dikunci"
                              : s.dicentang
                              ? `Lepas centang (${rp(s.rate)})`
                              : `Centang (${rp(s.rate)})`
                          }
                          className={cx(
                            "mx-auto flex h-9 w-full max-w-[104px] flex-col items-center justify-center rounded-lg border text-[11px] font-black transition",
                            s.dicentang
                              ? "border-primary/40 bg-primary/10 text-primary"
                              : "border-slate-200 text-slate-400 hover:border-primary/40 dark:border-slate-700",
                            data.terkunci && "cursor-not-allowed opacity-60"
                          )}
                        >
                          <Icon
                            name={s.dicentang ? "check_box" : "check_box_outline_blank"}
                            className={cx("text-base", s.dicentang && "anim-tick")}
                          />
                          <span className={cx(!Number(s.rate) && "text-amber-600")}>
                            {Number(s.rate) ? rp(s.rate) : "tarif 0"}
                          </span>
                        </button>
                      </Td>
                    ))}
                    <Td right>
                      <span className="font-black">{rp(total)}</span>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </DataCard>
  );
}

// ── Rekap bulanan ───────────────────────────────────────────────────────────
function TabRekap({ setToast, versi, isSuperAdmin = false }) {
  const kini = new Date();
  const [year, setYear] = useState(kini.getFullYear());
  const [month, setMonth] = useState(kini.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const res = await api.getStaffRecap({ year, month });
      if (res?.success) setData(res);
      else setToast({ type: "error", message: res?.message || "Gagal memuat" });
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal memuat" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, versi]);

  async function kunci() {
    if (!window.confirm(
      `Kunci ${BULAN[month - 1]} ${year}? Kuncinya BERSAMA dengan Trainer Payroll: gaji `
      + "trainer dihitung ulang sekali lagi, lalu gaji trainer & staff bulan ini dibekukan — "
      + "centang, tarif, absensi, permintaan, dan insentif/potongan tak bisa diubah lagi. "
      + "Hanya super admin yang bisa membukanya."
    )) return;
    try {
      const res = await api.lockStaffMonth({ year, month });
      setToast({
        type: res?.success === false ? "error" : "success",
        message: res?.message || "Terkunci."
      });
      load();
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal mengunci" });
    }
  }

  async function buka() {
    if (!window.confirm(
      `Buka kembali ${BULAN[month - 1]} ${year}? Gaji trainer & staff bulan ini bisa `
      + "berubah lagi, padahal mungkin sudah dibayarkan."
    )) return;
    try {
      const res = await api.unlockStaffMonth({ year, month });
      setToast({
        type: res?.success === false ? "error" : "success",
        message: res?.message || "Dibuka."
      });
      load();
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal membuka" });
    }
  }

  // Ekspor dibangun di sisi klien dari data yang SUDAH tampil, bukan lewat
  // permintaan baru ke server. Dengan begitu berkasnya dijamin sama persis
  // dengan yang dilihat admin di layar — tak ada celah untuk dua angka berbeda.
  async function ekspor() {
    if (!baris.length) {
      setToast({ type: "error", message: "Tak ada yang bisa diekspor." });
      return;
    }
    try {
      const ExcelJS = (await import("exceljs")).default;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Staff Payroll");
      const sesi = data?.sessions || [];

      ws.addRow([`Staff Payroll — ${BULAN[month - 1]} ${year}`]);
      if (data?.terkunci) ws.addRow(["TERKUNCI"]);
      ws.addRow([]);
      ws.addRow(["Nama", "Jabatan", ...sesi, "Jumlah Sesi", "Total"]);

      baris.forEach((b) => {
        ws.addRow([
          b.nama,
          b.staffPosition || "",
          ...sesi.map((s) => Number(b.perSesi?.[s] || 0)),
          b.jumlahSesi,
          Number(b.total || 0)
        ]);
      });

      ws.addRow([]);
      ws.addRow(["TOTAL CABANG", "", ...sesi.map(() => ""), "", Number(data?.totalCabang || 0)]);

      ws.getRow(4).font = { bold: true };
      ws.columns.forEach((k, i) => {
        k.width = i === 0 ? 24 : i === 1 ? 18 : 13;
      });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Staff Payroll ${BULAN[month - 1]} ${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal mengekspor" });
    }
  }

  async function segarkan() {
    try {
      const res = await api.refreshStaffRates({ year, month });
      setToast({
        type: res?.success === false ? "error" : "success",
        message: res?.message || "Selesai."
      });
      load();
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal menyegarkan" });
    }
  }

  const baris = data?.baris || [];
  const kilatCabang = useFlashOnChange(Number(data?.totalCabang || 0));

  return (
    <DataCard>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-5 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <Select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="w-auto">
            {BULAN.map((b, i) => (
              <option key={b} value={i + 1}>
                {b}
              </option>
            ))}
          </Select>
          <Input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="w-24"
          />
          {data?.terkunci && <Badge>Terkunci</Badge>}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-sm">
            <span className="text-slate-500">Total cabang: </span>
            <span className={cx("font-black text-primary", kilatCabang && "anim-flash")}>
              {rp(data?.totalCabang || 0)}
            </span>
          </div>

          <Button
            variant="secondary"
            onClick={ekspor}
            disabled={loading || !baris.length}
            title="Unduh rekap bulan ini sebagai Excel"
          >
            <Icon name="download" className="text-base" />
            Excel
          </Button>

          {/* Mengunci: admin cabang, sebab dialah yang tahu bulannya sudah beres.
              Membuka: super admin saja — tombolnya memang tak muncul untuk yang
              lain, dan servernya menolak walau permintaannya dipaksakan. */}
          {!data?.terkunci ? (
            <Button onClick={kunci} disabled={loading || !baris.length}>
              <Icon name="lock" className="text-base" />
              Kunci Bulan
            </Button>
          ) : isSuperAdmin ? (
            <Button variant="secondary" onClick={buka} disabled={loading}>
              <Icon name="lock_open" className="text-base" />
              Buka Kunci
            </Button>
          ) : (
            <span className="text-xs text-slate-500">
              Minta super admin untuk membukanya.
            </span>
          )}
        </div>
      </div>

      {/* Tarif 0 disebut terpisah: "totalnya kecil" dan "tarifnya belum diisi" itu dua
          masalah yang tindakannya berbeda, dan yang kedua masih bisa dibetulkan. */}
      {!!data?.tarifKosong && !data?.terkunci && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-300/40 bg-amber-500/10 px-5 py-3">
          <span className="text-xs font-bold text-amber-700 dark:text-amber-400">
            {data.tarifKosong} centang masih bertarif 0 — tarifnya belum diisi saat dicentang.
          </span>
          <Button onClick={segarkan}>
            <Icon name="refresh" className="mr-1 align-middle text-base" />
            Segarkan Tarif
          </Button>
        </div>
      )}

      {loading ? (
        <div className="p-5">
          <Spinner />
        </div>
      ) : !baris.length ? (
        <div className="p-5">
          <Empty label="Belum ada sesi yang dicentang di bulan ini." />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] whitespace-nowrap text-left">
            <thead>
              <tr>
                <Th>Staff</Th>
                <Th>Jabatan</Th>
                <Th center>Jumlah Sesi</Th>
                <Th right>Total</Th>
              </tr>
            </thead>
            <tbody>
              {baris.map((b) => (
                <tr
                  key={b.userId}
                  className="border-t border-slate-200 hover:bg-primary/[0.03] dark:border-slate-800 dark:hover:bg-primary/[0.06]"
                >
                  <Td>
                    <span className="font-bold">{b.nama}</span>
                  </Td>
                  <Td>{b.staffPosition || "—"}</Td>
                  <Td center>
                    {b.jumlahSesi}
                    {!!b.tarifKosong && (
                      <span className="ml-2 text-[10px] font-black text-amber-600">
                        {b.tarifKosong} tarif 0
                      </span>
                    )}
                  </Td>
                  <Td right>
                    <span className="font-black">{rp(b.total)}</span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DataCard>
  );
}

// ── Penunjukan staff & sesi yang tak dibayar trainer ────────────────────────
function TabOrang({ setToast, onBerubah }) {
  const [orang, setOrang] = useState([]);
  const [jabatan, setJabatan] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [atur, setAtur] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.getStaffList(), api.getStaffSessions()]);
      if (a?.success) {
        setOrang(a.orang || []);
        setJabatan(a.jabatan || []);
      } else {
        setToast({ type: "error", message: a?.message || "Gagal memuat" });
      }
      if (b?.success) setSessions(b.sessions || []);
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal memuat" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function selesai(pesan, tipe = "success") {
    if (pesan) setToast({ type: tipe, message: pesan });
    setAtur(null);
    load();
    onBerubah();
  }

  const staff = orang.filter((o) => o.staffPosition);
  const calon = orang.filter((o) => !o.staffPosition);

  if (loading) return <Spinner />;

  return (
    <>
      <DataCard className="mb-6">
        <div className="border-b border-slate-200 p-5 dark:border-slate-800">
          <div className="font-black">Staff per-sesi di cabang ini</div>
          <p className="mt-1 text-xs text-slate-500">
            Jabatannya menentukan tarif. Sesi yang dicabut TIDAK dibayar sebagai gaji
            trainer — dipakai untuk orang yang jam mengajarnya sudah ditanggung gaji staff.
          </p>
        </div>

        {!staff.length ? (
          <div className="p-5">
            <Empty label="Belum ada yang ditunjuk." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] whitespace-nowrap text-left">
              <thead>
                <tr>
                  <Th>Nama</Th>
                  <Th>Jabatan Staff</Th>
                  <Th>Sesi yang tak dibayar trainer</Th>
                  <Th right>Atur</Th>
                </tr>
              </thead>
              <tbody>
                {staff.map((o) => (
                  <tr key={o.userId} className="border-t border-slate-200 dark:border-slate-800">
                    <Td>
                      <span className="font-bold">{o.nama}</span>
                    </Td>
                    <Td>{o.staffPosition}</Td>
                    <Td>
                      {o.sesiDicabut.length ? (
                        <span className="whitespace-normal text-xs">
                          {o.sesiDicabut.join(" · ")}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">
                          tidak ada — gaji trainernya penuh
                        </span>
                      )}
                    </Td>
                    <Td right>
                      <Button title={`Atur ${o.nama}`} onClick={() => setAtur(o)}>
                        <Icon name="tune" />
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DataCard>

      <DataCard delay={1}>
        <div className="border-b border-slate-200 p-5 dark:border-slate-800">
          <div className="font-black">Belum ditunjuk</div>
          <p className="mt-1 text-xs text-slate-500">
            Akun admin & team leader di cabang ini yang belum digaji per sesi.
          </p>
        </div>
        {!calon.length ? (
          <div className="p-5">
            <Empty label="Semuanya sudah ditunjuk." />
          </div>
        ) : (
          <div className="divide-y divide-slate-200 dark:divide-slate-800">
            {calon.map((o) => (
              <div key={o.userId} className="flex items-center justify-between gap-3 px-5 py-3">
                <div>
                  <div className="font-bold">{o.nama}</div>
                  <div className="text-[11px] text-slate-500">{o.peran.join(", ")}</div>
                </div>
                <Button onClick={() => setAtur(o)}>Tunjuk</Button>
              </div>
            ))}
          </div>
        )}
      </DataCard>

      {atur && (
        <DialogAtur
          orang={atur}
          jabatan={jabatan}
          sessions={sessions}
          setToast={setToast}
          onClose={() => setAtur(null)}
          onSelesai={selesai}
        />
      )}
    </>
  );
}

function DialogAtur({ orang, jabatan, sessions, setToast, onClose, onSelesai }) {
  const [nama, setNama] = useState(orang.staffPosition || "");
  const [dicabut, setDicabut] = useState(new Set(orang.sesiDicabut || []));
  const [simpan, setSimpan] = useState(false);

  // Semua sesi cabang ini + yang terlanjur dicabut walau kelasnya sudah tak ada,
  // supaya tak ada pencabutan yang menggantung tanpa bisa dibatalkan.
  const daftar = [...new Set([...sessions, ...(orang.sesiDicabut || [])])].sort();

  function toggle(s) {
    setDicabut((lama) => {
      const baru = new Set(lama);
      if (baru.has(s)) baru.delete(s);
      else baru.add(s);
      return baru;
    });
  }

  async function kirim() {
    setSimpan(true);
    try {
      const res = await api.setStaffPosition({
        userId: orang.userId,
        staffPosition: nama.trim()
      });
      if (res?.success === false) {
        setToast({ type: "error", message: res.message });
        setSimpan(false);
        return;
      }
      if (!nama.trim()) {
        // Dicabut dari staff → server sudah membatalkan pencabutan sesinya.
        onSelesai(res.message);
        return;
      }
      const res2 = await api.setStaffBlocks({
        userId: orang.userId,
        sessions: [...dicabut]
      });
      onSelesai(
        res2?.success === false ? res2.message : `${res.message} ${res2.message}`,
        res2?.success === false ? "error" : "success"
      );
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal menyimpan" });
      setSimpan(false);
    }
  }

  return (
    <Modal title={`Atur — ${orang.nama}`} open onClose={onClose} wide>
      <div className="space-y-5">
        <div>
          <div className="mb-1 text-xs font-black uppercase tracking-wider text-slate-500">
            Jabatan staff
          </div>
          <div className="flex gap-2">
            <Select
              value={jabatan.includes(nama) ? nama : ""}
              onChange={(e) => setNama(e.target.value)}
              className="w-auto"
            >
              <option value="">— ketik sendiri —</option>
              {jabatan.map((j) => (
                <option key={j} value={j}>
                  {j}
                </option>
              ))}
            </Select>
            <Input
              value={nama}
              placeholder="mis. Admin, IT Support"
              onChange={(e) => setNama(e.target.value)}
            />
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Kosongkan untuk mencabut orang ini dari gaji per sesi. Pencabutan sesinya ikut
            dibatalkan, jadi gaji trainernya kembali penuh.
          </p>
        </div>

        {!!nama.trim() && (
          <div>
            <div className="mb-1 text-xs font-black uppercase tracking-wider text-slate-500">
              Sesi yang TIDAK dibayar sebagai gaji trainer
            </div>
            <p className="mb-2 text-xs text-slate-500">
              Centang sesi yang jam mengajarnya sudah ditanggung gaji staff. Yang tidak
              dicentang tetap dibayar seperti trainer biasa — termasuk kelas malam.
            </p>
            {!daftar.length ? (
              <p className="text-xs text-slate-400">Belum ada kelas di cabang ini.</p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {daftar.map((s) => (
                  <button
                    key={s}
                    onClick={() => toggle(s)}
                    className={cx(
                      "flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm font-bold transition",
                      dicabut.has(s)
                        ? "border-red-400/50 bg-red-500/10 text-red-600 dark:text-red-400"
                        : "border-slate-200 text-slate-500 hover:border-primary/40 dark:border-slate-700"
                    )}
                  >
                    <Icon
                      name={dicabut.has(s) ? "check_box" : "check_box_outline_blank"}
                      className="text-base"
                    />
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button onClick={onClose} className="bg-slate-200 text-slate-600 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-200">
            Batal
          </Button>
          <Button onClick={kirim} disabled={simpan}>
            {simpan ? "Menyimpan..." : "Simpan"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
